"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteUserActionManager,
  updateUserActionManager,
} from "../../../../services/action/manager";
import CustomModal from "../../../utils/modals/CustomModal";
import FormEditExpert from "./components/FormEditExpert";
import PersonalCard from "../../../card/PersonalCard";
import FullScreenModal from "../../../utils/modals/FullScreenModal";
import DeleteConfirmation from "../../../utils/dialoge/DeleteConfirmation";
import {
  Plus,
  User,
  Mail,
  Briefcase,
  ChevronDown,
  ChevronUp,
  Users,
  Activity,
  UserCheck,
  Clock,
  ArrowUpDown,
  Phone,
  Menu,
} from "lucide-react";
import { returnDateWithTime } from "../../../utils/functions/functions";
import Paginations from "../../../utils/pagination/Paginations";
import socket from "../../../socket/socket";
import { handlePopupModalAction } from "../../../../services/action/event";

// Custom Toggle Switch Component
const ToggleSwitch = ({ checked, onChange, size = "default" }) => {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        onChange={onChange}
      />
      <div
        className={`
          ${size === "small" ? "w-8 h-4" : "w-11 h-6"} 
          rounded-full 
          peer 
          transition-all
          duration-200
          shadow-inner
          ${
            checked
              ? "bg-gradient-to-r from-green-400 to-green-600"
              : "bg-gray-200"
          }
          after:content-[''] 
          after:absolute 
          after:top-[2px] 
          after:left-[2px] 
          after:bg-white 
          after:rounded-full 
          after:h-${size === "small" ? "3" : "5"} 
          after:w-${size === "small" ? "3" : "5"} 
          after:shadow-md
          after:transition-all
          after:duration-300
          peer-checked:after:translate-x-full
          ${checked ? "after:border-green-200" : "after:border-gray-200"}
        `}
      ></div>
    </label>
  );
};

const ManagerListComponents = ({
  getList,
  TeamsData,
  listTitle = "Manager List",
}) => {
  const { isDeleteUser, isListLoading } = useSelector(
    (state) => state.managerReducer
  );
  const { user } = useSelector((state) => state.authenticationReducer);
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useDispatch();
  const [operatorDetail, setExpertDetails] = useState({
    modal: false,
    preview: false,
    edit: false,
    data: "",
  });
  const [deleteId, setDeleteId] = useState("");
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [sortedData, setSortedData] = useState([]);

  useEffect(() => {
    socket.on("internal_user_activity", ({ userType }) => {
      if (userType == "MANAGER") {
        getList({
          firstTime: true,
          limit: 10,
          currentPage: 1,
        });
      }
    });
    return () => {
      socket.off("internal_user_activity");
    };
  }, [getList]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      setIsMobile(width < 768);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  // Sort data when TeamsData or sortConfig changes
  useEffect(() => {
    if (TeamsData?.data) {
      let sortableData = [...TeamsData.data];

      if (sortConfig.key) {
        sortableData.sort((a, b) => {
          // Handle nested properties
          const aValue = sortConfig.key.includes(".")
            ? sortConfig.key.split(".").reduce((obj, key) => obj?.[key], a)
            : a[sortConfig.key];
          const bValue = sortConfig.key.includes(".")
            ? sortConfig.key.split(".").reduce((obj, key) => obj?.[key], b)
            : b[sortConfig.key];

          // Handle different data types
          if (aValue === null || aValue === undefined)
            return sortConfig.direction === "ascending" ? 1 : -1;
          if (bValue === null || bValue === undefined)
            return sortConfig.direction === "ascending" ? -1 : 1;

          if (typeof aValue === "string" && typeof bValue === "string") {
            return sortConfig.direction === "ascending"
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          }

          return sortConfig.direction === "ascending"
            ? aValue > bValue
              ? 1
              : -1
            : aValue < bValue
            ? 1
            : -1;
        });
      }
      setSortedData(sortableData);
    }
  }, [TeamsData?.data, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (name) => {
    if (sortConfig.key !== name)
      return <ArrowUpDown className="w-3 h-3 opacity-50" />;
    return sortConfig.direction === "ascending" ? (
      <ChevronUp className="w-3 h-3" />
    ) : (
      <ChevronDown className="w-3 h-3" />
    );
  };

  const onPagination = (currentPage) => {
    getList({ currentPage });
    setCurrentPage(currentPage);
  };

  const afterSave = () => {
    getList({ currentPage, isEmptylist: false });
    setExpertDetails({});
  };

  const userType = user?.userType?.toLowerCase();

  const deleteFinal = () => {
    dispatch(
      deleteUserActionManager({
        userType,
        data: deleteId,
        cb: () => {
          setDeleteId("");
          getList({ currentPage, isEmptylist: false });
          setExpertDetails({});
        },
      })
    );
  };

  const updateStatus = (data) => {
    dispatch(
      updateUserActionManager({
        userType,
        data: {
          _id: data?._id,
          status: data?.status === "ACTIVE" ? "NOT ACTIVE" : "ACTIVE",
        },
        cb: () => getList({ currentPage, isEmptylist: false }),
      })
    );
  };

  // Mobile card view for each manager
  const renderMobileManagerCard = (manager, index) => (
    <div
      key={manager._id}
      className="bg-white rounded-lg shadow-sm mb-3 overflow-hidden"
    >
      <div className="flex items-center justify-between p-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200">
            <img
              src={manager.image || "/placeholder.svg"}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <button
              onClick={() =>
                setExpertDetails({ data: manager, preview: true, modal: false })
              }
              className="text-sm font-medium text-blue-600 flex items-center gap-1"
            >
              {manager?.name || "NA"}
            </button>
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
              <Mail className="w-3 h-3" />
              <span className="truncate max-w-[150px]">
                {manager?.email || "NA"}
              </span>
            </div>
          </div>
        </div>
        <div
          className={`flex items-center gap-1 ${
            manager.isOnline ? "text-green-600" : "text-red-600"
          }`}
        >
          <div
            className={`w-2 h-2 rounded-full ${
              manager.isOnline ? "bg-green-500" : "bg-red-500"
            }`}
          />
          <span className="text-xs font-medium">
            {manager.isOnline ? "Online" : "Offline"}
          </span>
        </div>
      </div>

      <div className="p-3 grid grid-cols-2 gap-3">
        <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
          <div className="flex items-center gap-1.5">
            <Briefcase className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs text-gray-700">
              {manager?.userType || "NA"}
            </span>
          </div>
          <span className="text-xs font-medium text-blue-600">
            {manager?.number || "NA"}
          </span>
        </div>

        <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
          <span className="text-xs text-gray-700">Status</span>
          <ToggleSwitch
            size="small"
            checked={manager.status === "ACTIVE"}
            onChange={() => updateStatus(manager)}
          />
        </div>
      </div>

      <div className="flex border-t border-gray-100">
        <button
          onClick={() =>
            setExpertDetails({ data: manager, preview: true, modal: false })
          }
          className="flex-1 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center gap-1"
        >
          <User className="w-3.5 h-3.5" />
          View Details
        </button>
        <div className="w-px bg-gray-100"></div>
        <button
          onClick={() =>
            setExpertDetails({
              edit: true,
              modal: true,
              preview: false,
              data: manager,
            })
          }
          className="flex-1 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center gap-1"
        >
          <Activity className="w-3.5 h-3.5" />
          Edit Manager
        </button>
      </div>
    </div>
  );

  // Table headers with sorting
  const tableHeaders = [
    { key: "isOnline", label: "Status" },
    { key: null, label: "Sr." },
    { key: "contactDetails", label: "Contact" },
    { key: null, label: "Image" },
    { key: "name", label: "Name" },
    { key: "lastLogin", label: "Last Login" },
    { key: "email", label: "Email" },
    { key: "number", label: "Number" },
    { key: "userType", label: "Role" },
    { key: null, label: "Actions" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 text-gray-900 p-2 sm:p-4">
      <div className="rounded-lg overflow-hidden bg-white shadow-xl">
        {deleteId && (
          <DeleteConfirmation
            open={deleteId}
            isDeleteLoading={isDeleteUser}
            onClose={() => setDeleteId("")}
            onConfirm={deleteFinal}
          />
        )}

        <CustomModal
          title={"Add Manager"}
          bgContent="dark-bg"
          open={operatorDetail.modal}
          onClose={() => setExpertDetails({})}
          content={() => (
            <FormEditExpert data={operatorDetail?.data} close={afterSave} />
          )}
        />

        <FullScreenModal
          title={"Personal Details"}
          bgContent="dark-bg"
          size="xl"
          open={operatorDetail.preview}
          onClose={() => setExpertDetails({})}
          content={() => (
            <PersonalCard
              onDeleteClick={(data) => setDeleteId(data)}
              onEditClick={(data) =>
                setExpertDetails({
                  edit: true,
                  modal: true,
                  preview: false,
                  data,
                })
              }
              data={operatorDetail?.data}
              close={() => setExpertDetails({})}
            />
          )}
        />

        <div className="p-3 sm:p-4 border-b border-gray-200 bg-white">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="flex items-center gap-2 w-full justify-center sm:justify-start">
              <button
                onClick={() =>
                  dispatch(handlePopupModalAction({ isOpenSidebar: true }))
                }
                className="mr-4 p-2 hover:text-[#fff]  rounded-md hover:bg-[#0A2547] lg:hidden"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-600">
                {listTitle}
              </h2>
              <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-full">
                <Users className="w-3 h-3 text-blue-500" />
                <span className="text-xs font-medium text-blue-700">
                  Total: {TeamsData?.pagination?.total || 0}
                </span>
              </div>
            </div>
            <div className="flex flex-row gap-2 w-full justify-between sm:justify-end">
              <button
                onClick={() => setExpertDetails({ modal: true, edit: false })}
                className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full text-xs sm:text-sm font-medium flex items-center justify-center gap-1 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{windowWidth < 640 ? "Add" : "Add Manager"}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Card */}
        <div className="p-2 sm:p-4 bg-white border-b border-gray-100">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-1.5 sm:p-2 flex items-center justify-between shadow-sm">
              <div>
                <p className="text-xs text-blue-600 font-medium">Total</p>
                <h3 className="text-sm sm:text-base font-bold text-gray-800">
                  {TeamsData?.pagination?.total || 0}
                </h3>
                <p className="text-[9px] text-gray-500 hidden sm:block">
                  Managers
                </p>
              </div>
              <div className="bg-blue-100 p-1 sm:p-1.5 rounded-full">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-1.5 sm:p-2 flex items-center justify-between shadow-sm">
              <div>
                <p className="text-xs text-green-600 font-medium">Active</p>
                <h3 className="text-sm sm:text-base font-bold text-gray-800">
                  {TeamsData?.data?.filter(
                    (manager) => manager.status === "ACTIVE"
                  ).length || 0}
                </h3>
                <p className="text-[9px] text-gray-500 hidden sm:block">
                  In system
                </p>
              </div>
              <div className="bg-green-100 p-1 sm:p-1.5 rounded-full">
                <UserCheck className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-1.5 sm:p-2 flex items-center justify-between shadow-sm">
              <div>
                <p className="text-xs text-purple-600 font-medium">Online</p>
                <h3 className="text-sm sm:text-base font-bold text-gray-800">
                  {TeamsData?.data?.filter((manager) => manager.isOnline)
                    .length || 0}
                </h3>
                <p className="text-[9px] text-gray-500 hidden sm:block">
                  Right now
                </p>
              </div>
              <div className="bg-purple-100 p-1 sm:p-1.5 rounded-full">
                <div className="relative">
                  <User className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                  <div className="absolute -top-1 -right-1 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile view */}
        {isMobile && (
          <div className="p-2">
            {sortedData.map((manager, index) =>
              renderMobileManagerCard(manager, index)
            )}
            {isListLoading && (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            )}
            {TeamsData?.pagination && (
              <div className="mt-4">
                <Paginations
                  onChange={onPagination}
                  pagination={TeamsData?.pagination}
                />
              </div>
            )}
          </div>
        )}

        {/* Desktop view */}
        {!isMobile && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  {tableHeaders.map((header, index) => (
                    <th
                      key={index}
                      className={`px-2 sm:px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                        header.key ? "cursor-pointer hover:bg-gray-100" : ""
                      }`}
                      onClick={() => header.key && requestSort(header.key)}
                    >
                      <div className="flex items-center gap-1">
                        {header.label}
                        {header.key && getSortIcon(header.key)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedData.map((manager, index) => (
                  <tr
                    key={manager._id}
                    className="bg-white hover:bg-gray-50 transition-all duration-300"
                  >
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div
                          className={`flex items-center gap-1 ${
                            manager.isOnline ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full ${
                              manager.isOnline ? "bg-green-500" : "bg-red-500"
                            }`}
                          />
                          <span className="text-xs font-medium">
                            {manager.isOnline ? "Online" : "Offline"}
                          </span>
                        </div>
                        <ToggleSwitch
                          checked={manager.status === "ACTIVE"}
                          onChange={() => updateStatus(manager)}
                        />
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                      {index + 1}
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs">
                      {manager?.contactDetails?.length ? (
                        <span className="px-1 sm:px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-green-100 text-green-800">
                          Yes
                        </span>
                      ) : (
                        <span className="px-1 sm:px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          NA
                        </span>
                      )}
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                      <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-200 transition-transform duration-300 hover:scale-110 hover:border-blue-500">
                        <img
                          src={manager.image || "/placeholder.svg"}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                      <button
                        onClick={() =>
                          setExpertDetails({
                            data: manager,
                            preview: true,
                            modal: false,
                          })
                        }
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 flex items-center gap-1 hover:underline max-w-[120px] truncate"
                      >
                        <User className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">
                          {manager?.name || "NA"}
                        </span>
                      </button>
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                      {returnDateWithTime(manager?.lastLogin)}
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                      <div className="flex items-center gap-1 max-w-[150px]">
                        <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="truncate">
                          {manager?.email || "NA"}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                      {manager?.number || "NA"}
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                      <div className="flex items-center gap-1 max-w-[100px]">
                        <Briefcase className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="truncate">
                          {manager?.userType || "NA"}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() =>
                            setExpertDetails({
                              edit: true,
                              modal: true,
                              preview: false,
                              data: manager,
                            })
                          }
                          className="p-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
                        >
                          <Activity className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteId(manager._id)}
                          className="p-1 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-3.5 h-3.5"
                          >
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {isListLoading && (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            )}
            {TeamsData?.pagination && (
              <div className="p-4 border-t border-gray-200">
                <Paginations
                  onChange={onPagination}
                  pagination={TeamsData?.pagination}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerListComponents;
